import { useState } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { createQueryClient } from './providers/query-client'
import { RootErrorBoundary } from './providers/RootErrorBoundary'
import { buildRouter } from './router/router'
import { setupHttp } from '@/shared/api'
import { OfflineBanner } from '@/widgets/offline-banner'
import { Toaster } from '@/widgets/toaster'

/**
 * `setupHttp` вешает перехватчики на МОДУЛЬНЫЙ синглтон сгенерированного клиента
 * (`shared/api/generated/client.gen.ts`) — вызвать его дважды значит зарегистрировать
 * перехватчики дважды, а не переписать их.
 *
 * ⚠️ `useState(() => { setupHttp(...); return router })` для этого НЕ ХВАТАЕТ: он защищает от вызова
 * на каждом ре-рендере, но не от StrictMode в dev — тот намеренно дважды зовёт ленивый
 * инициализатор. Поэтому `setupHttp` стоит на уровне модуля, один раз за жизнь вкладки. Какой роутер
 * использовать при 401 — в изменяемой ссылке: какой бы из вызовов инициализатора React ни оставил
 * настоящим состоянием, ссылка обновится тем же вызовом.
 */
let handleUnauthorized: (() => void) | null = null
setupHttp(() => handleUnauthorized?.())

export function App() {
  const [queryClient] = useState(createQueryClient)
  const [router] = useState(() => {
    const r = buildRouter(queryClient)
    // Единственная реакция на 401: чистим кеш и уходим на вход,
    // запомнив, куда пользователь шёл.
    handleUnauthorized = () => {
      // На самой странице входа 401 — ЭТО ШТАТНЫЙ ОТВЕТ, а не сбой: она сама спрашивает
      // `GET /me`, чтобы понять, не вошёл ли пользователь уже, и «не вошёл» — ожидаемый исход.
      //
      // Запускать в ответ на него процедуру «вас разлогинило» (снести кеш и уйти на вход)
      // бессмысленно: мы уже на входе, и сносить нечего. Молчим.
      //
      // Бесконечной петли здесь НЕТ — замерено: за 3 секунды ровно 1 запрос к /api/me.
      if (location.pathname === '/login') return

      // `clear()`, а не `resetQueries()`: мы уходим со страницы, и данные нужно СТЕРЕТЬ, а не
      // перезапросить. `resetQueries()` дёрнул бы все активные запросы заново — каждый получил
      // бы свой 401 прямо во время навигации. При переключении компании всё наоборот, там
      // нужен именно `resetQueries()` — см. `features/company-switch`.
      queryClient.clear()
      const redirect = location.pathname + location.search
      void r.navigate({ to: '/login', search: { redirect }, replace: true })
    }
    return r
  })

  return (
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />

        {/* Вне роутера намеренно: и баннер, и тосты обязаны пережить смену экрана. Внутри они
            размонтировались бы вместе с ним — и сообщение об упавшей мутации исчезло бы ровно
            в тот момент, когда мутация увела человека на другую страницу. */}
        <OfflineBanner />
        <Toaster />
      </QueryClientProvider>
    </RootErrorBoundary>
  )
}
